import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TIMING_CATEGORIES_NAME } from 'shared/constants';
import { PermissionsService } from 'shared/services';
import { AlertNotice } from 'shared/components';
import { TcEventManualTiming } from 'features/tc-event-dashboard';
import { getPersonnelRoles, getPersonnelRolesName, checkForOnlineRooms } from 'shared/helpers';
import './TcEventStaffAndRooms.scss';
import ManageRooms from 'assets/svgs/manageRoomsWhite.svg';
import MonitorRooms from 'assets/svgs/monitorRoomsBlue.svg';

const checkNeededStaff = (rooms, staffCounts, requiresTechCoord) => {
  const neededTechCoord = requiresTechCoord && checkForOnlineRooms(rooms) ? 1 : 0;
  const neededRS = rooms.length;
  const neededProctors = rooms.reduce((acc, room) => {
    if (room.timing === TIMING_CATEGORIES_NAME.extended) {
      return acc + Math.floor((room.capacity - 1) / 20);
    } else {
      switch (true) {
        case room.capacity <= 35:
          return acc;
        case room.capacity <= 70:
          return acc + 1;
        case room.capacity <= 105:
          return acc + 2;
        case room.capacity <= 140:
          return acc + 3;
        case room.capacity <= 175:
          return acc + 4;
        case room.capacity <= 210:
          return acc + 5;
        default:
          return acc;
      }
    }
  }, 0);

  return (
    neededRS <= staffCounts.roomSupervisors &&
    neededProctors <= staffCounts.proctors &&
    neededTechCoord <= staffCounts.techCoordinator
  );
};

const getTestDayCoord = (staff) => {
  const personnelRoles = getPersonnelRoles();

  return staff.find((pers) => pers.roleName === personnelRoles.TEST_DAY_COORDINATOR);
};

const getTestDayCoordName = (staff) => {
  const tdCoord = getTestDayCoord(staff);

  if (tdCoord) {
    return `${tdCoord.lastName}, ${tdCoord.firstName}`;
  }
};

const getManualTimed = (rooms, manualTimerFlags) =>
  rooms.length && !isEmpty(manualTimerFlags)
    ? rooms.reduce(
        (total, value) => (manualTimerFlags[value.id]?.manualTimerFlag ? total + 1 : total),
        0
      )
    : 0;

const TcEventStaffAndRooms = ({
  event,
  staff,
  eventStaffCounts,
  rooms,
  locations,
  totalRooms,
  manualTimerFlags,
  nowManagedFlag,
  testCenterCycle,
}) => {
  const { t } = useTranslation();
  const programCode = event.testingEvent?.adminId?.cycleId?.programId?.code;
  const requiresTechCoord = programCode === 'actnatl' || programCode === 'actspecial';
  const isStaffed = checkNeededStaff(rooms, eventStaffCounts, requiresTechCoord);
  const anyRoomOnline = checkForOnlineRooms(rooms);
  const personnelRolesName = getPersonnelRolesName();
  const totalManualTimedRooms = getManualTimed(rooms, manualTimerFlags);
  const unlimitedFlag =
    event.unlimitedRovingProctorsFlag ||
    (testCenterCycle.cycle && testCenterCycle.cycle.unlimitedRovingProctorsFlag);

  return (
    <section className="event-staff-rooms-section">
      {(PermissionsService.hasTestCenterManage(event.testCenterCycleId) ||
        PermissionsService.hasEventManage(event.id)) && (
        <>
          {!isStaffed && (
            <div>
              <AlertNotice message={t('staffManagement.warningMessage')} type="warning" />
            </div>
          )}
          <div className="info-section">
            {!nowManagedFlag && (
              <div className="mb-1 info-row">
                <span className="fw-bold">{personnelRolesName.TEST_DAY_COORDINATOR}</span>
                {getTestDayCoordName(staff) || t('staffManagement.unassignedLabel')}
              </div>
            )}
            {(unlimitedFlag || rooms.length >= 8) && (
              <div className="mb-1 info-row">
                <span className="fw-bold">{personnelRolesName.PROCTOR_ROVING} </span>
                {eventStaffCounts.rovingProctors}
              </div>
            )}
            {anyRoomOnline && (
              <div className="mb-1 info-row">
                <span className="fw-bold">{personnelRolesName.TECH_COORDINATOR} </span>
                {eventStaffCounts.techCoordinator}
              </div>
            )}
            <div className="mb-1 mt-3 info-row">
              <span className="fw-bold">{t('testCenter.eventDashboard.roomsLabel')}</span>
              <span>{totalRooms}</span>
            </div>
            <div className="mb-1 info-row">
              <span className="fw-bold">{personnelRolesName.ROOM_SUPERVISOR} </span>
              <span>{eventStaffCounts.roomSupervisors}</span>
            </div>
            <div className="mb-1 info-row">
              <span className="fw-bold">{personnelRolesName.PROCTOR} </span>
              <span>{eventStaffCounts.proctors}</span>
            </div>
            <div className="mb-1 manual-timing-row">
              <div className="info-row">
                <span className="fw-bold ">{t('testCenter.eventDashboard.manualTimerRooms')} </span>
                <span className="pe-4">{totalManualTimedRooms}</span>
              </div>
              <div>
                {PermissionsService.hasManualTimerControl(event.testCenterCycleId, event.id) && (
                  <TcEventManualTiming locations={locations} rooms={rooms} />
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <div className="manage-buttons">
        {(PermissionsService.hasTestCenterManage(event.testCenterCycleId) ||
          PermissionsService.hasEventManage(event.id)) && (
          <Link
            to={`/test-event/${event.id}/staff-and-rooms`}
            href={`/test-event/${event.id}/staff-and-rooms`}
            className="btn btn-small btn-action">
            <ManageRooms />
            {t('testCenter.eventDashboard.manageRoomsAndStaff')}
          </Link>
        )}
        {(PermissionsService.hasTestCenterManage(event.testCenterCycleId) ||
          PermissionsService.hasEventManage(event.id) ||
          PermissionsService.hasRoomAccess(event.id)) &&
          (locations.length ? (
            <Link
              to={`/test-event/${event.id}/rooms-dashboard`}
              href={`/test-event/${event.id}/rooms-dashboard`}
              className="btn btn-small">
              <MonitorRooms />
              {t('testCenter.eventDashboard.monitorRoomsAndMaterials')}
            </Link>
          ) : (
            <button className="btn btn-small" disabled>
              {t('testCenter.eventDashboard.monitorRoomsAndMaterials')}
            </button>
          ))}
      </div>
    </section>
  );
};

export default TcEventStaffAndRooms;

TcEventStaffAndRooms.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          programId: PropTypes.shape({
            code: PropTypes.string,
          }),
        }),
      }),
    }),
  }).isRequired,
  staff: PropTypes.arrayOf(PropTypes.shape({})),
  rooms: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

TcEventStaffAndRooms.defaultProps = {
  staff: [],
};
